/**
 * Registry for the www Resources section (/resources).
 *
 * Resources are hand-built pages, not CMS content: every entry here has a matching
 * `frontend/components/www/resources/<category>/<slug>/Page.vue` and an explicit route in
 * `frontend/entrypoints/www/routes.ts`. Category subsections serve at top-level URL paths
 * (/<category>, e.g. /reports) even though the code stays under resources/. The registry only
 * carries the metadata the index pages, cards, and page chrome need — the body of each
 * resource lives in its Page.vue.
 *
 * Adding a report: add a `Report` entry to `REPORTS`, create its Page.vue (wrap it in
 * `ReportLayout`), register the route, and drop a hero image at
 * `frontend/assets/www/resources/reports/<slug>.jpg` (falls back to `_default.jpg`).
 *
 * Adding a tool: add a `Tool` entry to `TOOLS`. Ship it with `comingSoon: true` to announce it
 * on /tools without a page; drop the flag once `tools/<slug>/Page.vue` and its route
 * exist. Only live tools are linked anywhere.
 *
 * Adding a category: add it to `RESOURCE_CATEGORIES`, create `<category>/index/Page.vue`
 * plus a per-item `<category>/<slug>/Page.vue` convention like `reports/`, and extend
 * `resourcesInCategory` so the directory page can list it.
 */

export type ResourceCategoryKey = 'reports' | 'tools'

export interface ResourceCategory {
    key: ResourceCategoryKey
    name: string
    /** Short label used in eyebrows / breadcrumbs where the plural name is too long. */
    singular: string
    description: string
}

/** Every resource type shares this shape so cards and rails can render any of them. */
export interface Resource {
    category: ResourceCategoryKey
    slug: string
    title: string
    /** The dek shown under the headline and on cards. */
    description: string
    author: string
    /** ISO 8601 date (YYYY-MM-DD). Ordering is newest-first everywhere. */
    publishedAt: string
    /** Reading time only applies to editorial resources; tools omit it. */
    readingMinutes?: number
}

export type Report = Resource & {category: 'reports', readingMinutes: number}

export type Tool = Resource & {
    category: 'tools'
    /** One-line promise shown on the tool card, in the reader's own terms. */
    promise: string
    /** Announced but not built yet: listed on /tools, with no page and no link. */
    comingSoon?: boolean
}

export const RESOURCES_BRAND = {
    name: 'Resources',
    eyebrow: 'From the team at Osello',
    tagline: 'Reports, analysis, and tools for fashion brands selling everywhere.',
} as const

export const RESOURCE_CATEGORIES: readonly ResourceCategory[] = [
    {
        key: 'reports',
        name: 'Reports',
        singular: 'Report',
        description: 'Market analysis and original research on how premium marketplaces are changing for independent brands.',
    },
    {
        key: 'tools',
        name: 'Tools',
        singular: 'Tool',
        description: 'Free tools that answer a specific marketplace question about your own catalog, in your browser, in under a minute.',
    },
] as const

export const REPORTS: readonly Report[] = [
    {
        category: 'reports',
        slug: 'marketplace-fashion-brand-loyalty-report',
        title: 'Marketplace Fashion Brand Loyalty Report',
        description: 'We analyzed 211,523 marketplace orders from Shopify fashion brands selling on Nordstrom, Macy\'s, Bloomingdale\'s and Target Plus — and scanned 790 storefronts for loyalty programs. The cross-channel repeat purchase rate is 12%, no brand dashboard can see it, and only 1 in 4 brands runs a loyalty program.',
        author: 'Osello Team',
        publishedAt: '2026-08-25',
        readingMinutes: 10,
    },
    {
        category: 'reports',
        slug: 'cipa-fashion-brand-compliance-report',
        title: 'CIPA Marketplace Fashion Brand Compliance Report',
        description: 'Retail is now the most-sued industry under California’s CIPA, at $5,000 per violation. We scanned 699 independent fashion storefronts that sell on retail marketplaces like Nordstrom, Target+ and Macy\'s: 93% load the exact trackers named in these lawsuits, and barely one in four has any consent tooling.',
        author: 'Osello Team',
        publishedAt: '2026-08-23',
        readingMinutes: 9,
    },
]

export const TOOLS: readonly Tool[] = [
    {
        category: 'tools',
        slug: 'nordstrom-readiness-checker',
        title: 'Nordstrom readiness checker',
        promise: 'Paste a Shopify product URL. See which of Nordstrom’s required fields your data can actually fill.',
        description: 'A single clothing listing on Nordstrom needs 22 required fields, from UPC and NRF color code to material composition and washing instructions. This reads your live Shopify product and sorts every field into what is blocking you, what needs mapping, and what needs enriching.',
        author: 'Osello Team',
        publishedAt: '2026-08-30',
    },
    {
        category: 'tools',
        slug: 'shopify-image-checker',
        title: 'Shopify image checker',
        promise: 'Find out whether your product photography clears a marketplace’s image standards before you submit.',
        description: 'Ghost mannequin versus on-figure, white background, crops and aspect ratios, and whether every colorway has its own shot. See which images need recropping and which need reshooting.',
        author: 'Osello Team',
        publishedAt: '2026-08-30',
        comingSoon: true,
    },
    {
        category: 'tools',
        slug: 'nrf-color-code-finder',
        title: 'NRF color code finder',
        promise: 'Turn a colorway name into the NRF color code and color family retailers expect.',
        description: 'Department stores do not want “Gamay Serpent Print”, they want a color family and a numeric NRF code. Look up any colorway on your line sheet.',
        author: 'Osello Team',
        publishedAt: '2026-08-30',
        comingSoon: true,
    },
    {
        category: 'tools',
        slug: 'nrf-size-code-finder',
        title: 'NRF size code finder',
        promise: 'Turn any size on your size run into its five-digit NRF size code.',
        description: '6 Petite is 30206. A 32-inch waist with a 26-inch capri inseam is 10408. 4T is 40336. Look up the codes for a whole size run at once.',
        author: 'Osello Team',
        publishedAt: '2026-08-30',
        comingSoon: true,
    },
]

/** Tools with a built page. Coming-soon entries are listed on the tools index but never linked. */
export function liveTools(): Tool[] {
    return TOOLS.filter(tool => !tool.comingSoon)
}

function newestFirst<T extends Resource>(resources: readonly T[]): T[] {
    return [...resources].sort((a, b) => b.publishedAt.localeCompare(a.publishedAt) || a.title.localeCompare(b.title))
}

export function resourceCategory(key: ResourceCategoryKey): ResourceCategory {
    const category = RESOURCE_CATEGORIES.find(candidate => candidate.key === key)
    if (!category) throw new Error(`Unknown resource category: ${key}`)
    return category
}

export function resourceCategoryPath(key: ResourceCategoryKey): string {
    return `/${key}`
}

export function resourcePath(resource: Pick<Resource, 'category' | 'slug'>): string {
    return `/${resource.category}/${resource.slug}`
}

/** Newest-first resources for one category. Extend when a new category ships. */
export function resourcesInCategory(key: ResourceCategoryKey): Resource[] {
    switch (key) {
        case 'reports':
            return newestFirst(REPORTS)
        // Coming-soon tools are deliberately excluded: the directory links every resource it
        // lists, and an unbuilt tool has no page to link to.
        case 'tools':
            return newestFirst(liveTools())
    }
}

/** Every published resource across categories, newest first. */
export function allResources(): Resource[] {
    return newestFirst(RESOURCE_CATEGORIES.flatMap(category => resourcesInCategory(category.key)))
}

export function findReport(slug: string): Report {
    const report = REPORTS.find(candidate => candidate.slug === slug)
    if (!report) throw new Error(`Unknown report: ${slug}`)
    return report
}

export function findTool(slug: string): Tool {
    const tool = TOOLS.find(candidate => candidate.slug === slug)
    if (!tool) throw new Error(`Unknown tool: ${slug}`)
    return tool
}

export function formatResourceDate(publishedAt: string): string {
    const date = new Date(`${publishedAt}T00:00:00Z`)
    if (Number.isNaN(date.getTime())) return ''
    return date.toLocaleDateString('en-US', {month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC'})
}

// Hero images live at frontend/assets/www/resources/<category>/<slug>.jpg by convention;
// resources without one fall back to <category>/_default.jpg so a missing image never breaks
// the layout.
const heroImageModules = import.meta.glob<string>('@/assets/www/resources/*/*.jpg', {eager: true, import: 'default'})

export function resourceHeroImage(resource: Pick<Resource, 'category' | 'slug'>): string {
    const match = Object.entries(heroImageModules).find(([path]) => path.endsWith(`/${resource.category}/${resource.slug}.jpg`))
    if (match) return match[1]

    const fallback = Object.entries(heroImageModules).find(([path]) => path.endsWith(`/${resource.category}/_default.jpg`))
    return fallback ? fallback[1] : ''
}

// Author avatars live at frontend/assets/www/resources/authors/<author-slug>.png by
// convention; unknown authors fall back to the Osello mark.
const authorAvatarModules = import.meta.glob<string>('@/assets/www/resources/authors/*.png', {eager: true, import: 'default'})

// Short editorial bios for the end-note. Add an entry when a new author starts writing.
const AUTHOR_BIOS: Record<string, string> = {
    'Osello Team': 'The Osello team helps fashion brands get accepted, listed, and selling on premium marketplaces like Nordstrom, Macy’s, and Bloomingdale’s.',
}

export function resourceAuthorBio(author: string): string {
    return AUTHOR_BIOS[author] ?? ''
}

export function resourceAuthorAvatar(author: string): string {
    const slug = author.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
    const match = Object.entries(authorAvatarModules).find(([path]) => path.endsWith(`/${slug}.png`))
    if (match) return match[1]

    const fallback = Object.entries(authorAvatarModules).find(([path]) => path.endsWith('/osello-team.png'))
    return fallback ? fallback[1] : ''
}
